import type React from "react";
import type {ContractDocument, ContractLinkage, ContractParty} from "../contractExplorerTypes.ts";
import {CollapsibleSection} from "./CollapsibleSection.tsx";
import {PartyMatch} from "./PartyMatch.tsx";
import {SourceCite} from "./SourceCite.tsx";

type Props = {parties: ContractParty[]; linkage: ContractLinkage; documents: Record<string, ContractDocument>};

const ROLE_LABEL: Record<ContractParty['role'], string> = {
    supplier: 'Supplier', buyer: 'Buyer', other: 'Other party', unknown: 'Role unclear',
};

/** The documents that name the party, grouped by the role each one states. */
const statedRoles = (party: ContractParty): [string, string[]][] => {
    const byRole = new Map<string, string[]>();
    for (const [documentId, role] of Object.entries(party.stated)) {
        byRole.set(role, [...byRole.get(role) ?? [], documentId]);
    }
    return byRole.size ? [...byRole] : [[party.role, party.documents]];
};

/**
 * The companies the documents name, side by side with what linkage matched
 * them to: on the left the name and the role each document states, on the
 * right the supplier or buying entities it resolved to in the spend data.
 */
export const PartiesSection: React.FC<Props> = ({parties, linkage, documents}) => {
    if (!parties.length) return null;
    const buyers = parties.filter(p => p.used_as === 'buyer').length;
    // The parties linkage used come first: they are the ones the spend is matched on.
    const ordered = [...parties].sort((a, b) => Number(!a.used_as) - Number(!b.used_as));
    return (
        <CollapsibleSection title={`Parties (${parties.length})`}
                            sub="Who the documents name and in which role, and what linkage matched each one to in the spend data.">
            <div className="ci-card ci-parties">
                <div className="ci-party ci-party-head">
                    <span>Party</span>
                    <span>In the documents</span>
                    <span>Matched in the spend data</span>
                </div>
                {ordered.map(party => (
                    <div key={party.name} className="ci-party">
                        <span className="ci-party-n">{party.name}</span>
                        <span className="ci-party-s">
                            <span className="ci-party-tags">
                                {statedRoles(party).map(([role, ids]) => (
                                    <span key={role} className="ci-party-role">
                                        <span className="ci-party-r">{ROLE_LABEL[role as ContractParty['role']] ?? role}</span>
                                        {ids.map(id => <SourceCite key={id} documentId={id} documents={documents}/>)}
                                    </span>
                                ))}
                            </span>
                            {(party.evidence || party.note) && (
                                <span className="ci-party-e">
                                    {[party.evidence && `“${party.evidence}”`, party.note].filter(Boolean).join(' · ')}
                                </span>
                            )}
                        </span>
                        <PartyMatch party={party} linkage={linkage} buyers={buyers}/>
                    </div>
                ))}
            </div>
        </CollapsibleSection>
    );
};
